import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Text,
  Button,
  Spinner,
  Box,
  Divider,
  TextField,
  Checkbox,
  Banner,
  Thumbnail,
  InlineStack,
  Badge
} from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import api from '../utils/axiosConfig';

const MAX_PRODUCTS = 4;

function UpsellSettings() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [enabled, setEnabled] = useState(false);
  const [title, setTitle] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const searchTimer = useRef(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/upsell/config');
      const config = response.data.config || {};
      setEnabled(config.enabled === true);
      setTitle(config.title || '');
      setDiscountCode(config.discount_code || '');
      setSelectedProducts(Array.isArray(config.products) ? config.products : []);
    } catch (err) {
      console.error('Error loading upsell config:', err);
      setError(t('upsell.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const runSearch = useCallback(async (query) => {
    try {
      setSearching(true);
      const response = await api.get('/upsell/products', { params: { query } });
      setSearchResults(response.data.products || []);
    } catch (err) {
      console.error('Error searching products:', err);
      setError(t('upsell.searchError'));
    } finally {
      setSearching(false);
    }
  }, [t]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => runSearch(value), 400);
  };

  const addProduct = (product) => {
    setSelectedProducts((current) => {
      if (current.length >= MAX_PRODUCTS) return current;
      if (current.some((p) => p.variant_id === product.variant_id)) return current;
      return [...current, product];
    });
  };

  const removeProduct = (variantId) => {
    setSelectedProducts((current) => current.filter((p) => p.variant_id !== variantId));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      if (enabled && selectedProducts.length === 0) {
        setError(t('upsell.needProducts'));
        return;
      }

      await api.put('/upsell/config', {
        enabled,
        title: title.trim() || null,
        discount_code: discountCode.trim() || null,
        products: selectedProducts
      });

      setSuccess(t('upsell.saved'));
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error saving upsell config:', err);
      setError(err.response?.data?.error || t('upsell.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const formatPrice = (product) =>
    product.price ? `${product.price} ${product.currency || ''}`.trim() : '';

  if (loading) {
    return (
      <Card>
        <Box padding="4">
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
            <Spinner size="small" />
          </div>
        </Box>
      </Card>
    );
  }

  return (
    <Card>
      <Box padding="4">
        <Text variant="headingMd" as="h3">
          {t('upsell.sectionTitle')}
        </Text>
        <Text variant="bodySm" tone="subdued" as="p">
          {t('upsell.sectionDesc')}
        </Text>
      </Box>

      <Divider />

      <Box padding="4">
        {error && (
          <div style={{ marginBottom: '16px' }}>
            <Banner tone="critical" onDismiss={() => setError('')}>{error}</Banner>
          </div>
        )}
        {success && (
          <div style={{ marginBottom: '16px' }}>
            <Banner tone="success" onDismiss={() => setSuccess('')}>{success}</Banner>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Checkbox
            label={t('upsell.enableLabel')}
            checked={enabled}
            onChange={setEnabled}
          />

          <TextField
            label={t('upsell.titleLabel')}
            value={title}
            onChange={setTitle}
            placeholder={t('upsell.titlePlaceholder')}
            autoComplete="off"
            maxLength={255}
          />

          <TextField
            label={t('upsell.discountLabel')}
            value={discountCode}
            onChange={setDiscountCode}
            helpText={t('upsell.discountHelp')}
            autoComplete="off"
            maxLength={64}
          />

          <Divider />

          <div>
            <Text variant="bodyMd" fontWeight="semibold" as="p">
              {t('upsell.selectedProducts')} ({selectedProducts.length}/{MAX_PRODUCTS})
            </Text>
            <Text variant="bodySm" tone="subdued" as="p">
              {t('upsell.maxProducts')}
            </Text>

            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {selectedProducts.length === 0 && (
                <Text variant="bodySm" tone="subdued" as="p">
                  {t('upsell.noneSelected')}
                </Text>
              )}
              {selectedProducts.map((product) => (
                <div
                  key={product.variant_id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    border: '1px solid #e1e3e5',
                    borderRadius: '8px'
                  }}
                >
                  <InlineStack gap="3" blockAlign="center">
                    <Thumbnail
                      source={product.image_url || ''}
                      alt={product.title}
                      size="small"
                    />
                    <div>
                      <Text variant="bodyMd" as="p">{product.title}</Text>
                      <Text variant="bodySm" tone="subdued" as="p">{formatPrice(product)}</Text>
                    </div>
                  </InlineStack>
                  <Button variant="plain" tone="critical" onClick={() => removeProduct(product.variant_id)}>
                    {t('upsell.remove')}
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <TextField
            label={t('upsell.searchLabel')}
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={t('upsell.searchPlaceholder')}
            autoComplete="off"
            connectedRight={searching ? <Spinner size="small" /> : undefined}
          />

          {searchResults.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '320px', overflowY: 'auto' }}>
              {searchResults.map((product) => {
                const alreadySelected = selectedProducts.some((p) => p.variant_id === product.variant_id);
                return (
                  <div
                    key={product.variant_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      border: '1px solid #e1e3e5',
                      borderRadius: '8px',
                      backgroundColor: alreadySelected ? '#f6f6f7' : 'white'
                    }}
                  >
                    <InlineStack gap="3" blockAlign="center">
                      <Thumbnail
                        source={product.image_url || ''}
                        alt={product.title}
                        size="small"
                      />
                      <div>
                        <Text variant="bodyMd" as="p">{product.title}</Text>
                        <Text variant="bodySm" tone="subdued" as="p">{formatPrice(product)}</Text>
                      </div>
                    </InlineStack>
                    {alreadySelected ? (
                      <Badge tone="success">✓</Badge>
                    ) : (
                      <Button
                        size="slim"
                        onClick={() => addProduct(product)}
                        disabled={selectedProducts.length >= MAX_PRODUCTS}
                      >
                        {t('upsell.add')}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div>
            <Button variant="primary" onClick={handleSave} loading={saving}>
              {t('upsell.save')}
            </Button>
          </div>
        </div>
      </Box>
    </Card>
  );
}

export default UpsellSettings;
